import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase/client";
import { getSourcemapName as getSourcemapPath, parseStackTrace } from "@/utils/parseStackTrace";
import { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Json as SupabaseJson } from "@/lib/supabase/types";

export function ErrorBoundary2({ children, msg, extraInfo }: { children: React.ReactNode, msg: string, extraInfo?: object }) {
  return (
    <ErrorBoundary
      fallbackRender={({ error }) => <ErrorFallback error={error} msg={msg} extraInfo={extraInfo} />}
    >
      {children}
    </ErrorBoundary>
  );
}
function ErrorFallback({ error, msg, extraInfo }) {
  const { toast } = useToast();

  useEffect(() => {
    const sendError = async (error: unknown, isParsed: boolean = false, extraInfo?: object) => {
      let stack: string;
      let message: string;
      if (typeof error === "object") {
        if ("stack" in error) stack = error.stack as string;
        if ("message" in error) message = error.message as string;
      }
      console.log(error);
      const { error: supabaseError } = await supabase
        .from("errors")
        .insert({
          error: {
            stack: stack,
            message: message,
            msg: msg,
            stringified: JSON.stringify(error),
            extraInfo: extraInfo as SupabaseJson,
          },
          build_mode: import.meta.env.MODE,
          is_parsed: isParsed,
          user_agent: window.navigator.userAgent,
        });
      if (supabaseError) {
        toast({
          title: "Lyckades inte skicka felmeddelande automatiskt",
          description: "Om du ser ett rött felmeddelande någonstans med massor av text, vänligen ta en bild på det och skicka till vår support.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Någonting har gått fel",
          description: "Vänligen försök igen senare",
          variant: "destructive"
        });
      }
      console.log(`Sent${isParsed ? " parsed" : ""} error`);
    };
    const parseAndSendError = async () => {
      const parsedStack = await parseStackTrace(error.stack, getSourcemapPath());
      if (!parsedStack) {
        console.error("Failed to parse stack trace");
        return;
      };
      const parsedError = error;
      parsedError.stack = parsedStack;
      sendError(parsedError, true, extraInfo);
    };
    sendError(error, false, extraInfo);
    parseAndSendError();
  }, [error, extraInfo, msg, toast]);

  return (
    <div className="text-red-500 font-bold text-center text-lg">
      Ett oväntat fel har uppstått.<br /> Vänligen försök igen senare
    </div>
  );
}
